import { Box, Stack, Typography } from '@mui/material';
import { ColDef } from '@siloreal:modules:private/silobag-list/config/extra/types';
import { PLATFORM_GREEN, FALLBACK_STRING } from './column-field-ui';
import * as HOOKS from '@siloreal:shared:hooks';

const INSTRUMENT_FRAME = '#2f2b33';
const POINTER_OUTLINE = '#ffffff';

const BAR_LENGTH = 88;
const BAR_HEIGHT = 10;
const POINTER_WIDTH = 4;
const POINTER_HEIGHT = 5;
const POINTER_OUTLINE_WIDTH = 5;
const POINTER_OUTLINE_HEIGHT = 6;

const TUBE_SCALE_GRADIENT = `linear-gradient(to right,
  #c8e4a8 0%,
  #b8d898 16%,
  #ebe8b8 16%,
  #e8e0a8 32%,
  #e8d0a8 32%,
  #e0c0a0 58%,
  #e0b0a8 58%,
  #d8a8a8 70%,
  #c8c4c0 70%,
  #b8b4b0 100%)`;

const VIVID_SCALE_GRADIENT = `linear-gradient(to right,
  #8ed400 0%,
  ${PLATFORM_GREEN} 16%,
  #ffd800 16%,
  #f0c000 32%,
  #ff9a18 32%,
  #ff8810 58%,
  #ef3828 58%,
  #e83028 70%,
  #9a9498 70%,
  #8a8488 100%)`;

export function buildOriginMoistureColumn(): ColDef {
  return {
    field: 'initial_humidity',
    headerName: HOOKS.useLocale.getState().t('temp.common.bagging_humidity'),
    minWidth: 196,
    sortable: false,
    renderCell: ({ row }) => <OriginMoistureCell value={row.initial_humidity} />,
  };
}

function OriginMoistureCell({ value }: { value: number | null | undefined }) {
  const hasValue = value != null && !Number.isNaN(value);
  const percent = hasValue ? clamp(value, 0, 100) : 0;
  const isInvalid = hasValue && percent > 70;

  return (
    <Stack
      height="100%"
      alignItems="center"
      justifyContent="center"
      onClick={(event) => event.stopPropagation()}
      sx={{
        minWidth: 0,
        px: 1,
        opacity: isInvalid ? 0.62 : 1,
        filter: isInvalid ? 'grayscale(0.45)' : 'none',
      }}
    >
      {hasValue ? (
        <MoistureInstrument percent={percent} isInvalid={isInvalid} />
      ) : (
        <MoistureValueBadge percent={percent} hasValue={hasValue} isInvalid={isInvalid} />
      )}
    </Stack>
  );
}

function MoistureValueBadge({
  percent,
  hasValue,
  isInvalid,
}: {
  percent: number;
  hasValue: boolean;
  isInvalid: boolean;
}) {
  const accentColor = hasValue ? (isInvalid ? '#5c5760' : getVividMoistureColor(percent)) : '#a8a4a0';
  const label = hasValue ? formatMoisture(percent) : FALLBACK_STRING;

  const i18n = HOOKS.useLocale();
  const ORIGIN_HUMIDITY_REGISTERED = i18n.t('temp.common.bagging_humidity');
  const ORIGIN_HUMIDITY_NOT_REGISTERED = i18n.t('temp.common.bagging_humidity_not_registered');
  const title = hasValue ? `${label} ${ORIGIN_HUMIDITY_REGISTERED}` : ORIGIN_HUMIDITY_NOT_REGISTERED;

  return (
    <Box
      title={title}
      sx={{
        px: hasValue ? 0.85 : 0.75,
        py: hasValue ? 0.3 : 0.28,
        borderRadius: '4px',
        bgcolor: hasValue ? colorToRgba(accentColor, 0.16) : colorToRgba(accentColor, 0.06),
        border: hasValue ? `1px solid ${colorToRgba(accentColor, 0.32)}` : '1px dashed #b0aca6',
        boxShadow: hasValue ? `inset 0 1px 0 ${colorToRgba(accentColor, 0.14)}` : 'none',
      }}
    >
      <Typography
        noWrap
        sx={{
          fontFamily: '"Roboto Mono", "SF Mono", monospace',
          fontWeight: 700,
          fontSize: hasValue ? 13 : 12,
          letterSpacing: '0.05em',
          color: accentColor,
          lineHeight: 1.15,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function MoistureInstrument({ percent, isInvalid }: { percent: number; isInvalid: boolean }) {
  const markerLeft = `${clamp(percent, 0, 100)}%`;
  const pointerColor = getVividMoistureColor(percent);
  const zone = getMoistureZone(percent);
  const zoneWidth = zone.end - zone.start;
  const vividOffset = (zone.start / 100) * BAR_LENGTH;

  const i18n = HOOKS.useLocale();

  return (
    <Stack alignItems="center" gap={0.35} sx={{ bgcolor: 'transparent' }}>
      <MoistureValueBadge percent={percent} hasValue isInvalid={isInvalid} />

      <Box
        title={`${i18n.t('temp.private.list.common.humidity_level')}: ${formatMoisture(percent)}`}
        sx={{
          position: 'relative',
          width: BAR_LENGTH,
          bgcolor: 'transparent',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            height: POINTER_OUTLINE_HEIGHT + 2,
            mb: 0.2,
          }}
        >
          <MoisturePointer left={markerLeft} color={pointerColor} />
        </Box>

        <Box
          sx={{
            position: 'relative',
            width: BAR_LENGTH,
            height: BAR_HEIGHT,
            borderRadius: '3px',
            border: `1px solid ${INSTRUMENT_FRAME}`,
            background: TUBE_SCALE_GRADIENT,
            boxShadow: 'inset 0 1px 1px rgba(0,0,0,0.1)',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: `${zone.start}%`,
              top: 0,
              bottom: 0,
              width: `${zoneWidth}%`,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: BAR_LENGTH,
                height: BAR_HEIGHT,
                background: VIVID_SCALE_GRADIENT,
                transform: `translateX(-${vividOffset}px)`,
              }}
            />
          </Box>

          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(0,0,0,0.04) 100%)',
              pointerEvents: 'none',
            }}
          />
        </Box>
      </Box>
    </Stack>
  );
}

function MoisturePointer({ left, color }: { left: string; color: string }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        left,
        bottom: 0,
        transform: 'translateX(-50%)',
        width: 0,
        height: 0,
        zIndex: 2,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          bottom: 0,
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: `${POINTER_OUTLINE_WIDTH}px solid transparent`,
          borderRight: `${POINTER_OUTLINE_WIDTH}px solid transparent`,
          borderTop: `${POINTER_OUTLINE_HEIGHT}px solid ${POINTER_OUTLINE}`,
          filter: 'drop-shadow(0 1px 1px rgba(58,52,64,0.2))',
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          bottom: 1,
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: `${POINTER_WIDTH}px solid transparent`,
          borderRight: `${POINTER_WIDTH}px solid transparent`,
          borderTop: `${POINTER_HEIGHT}px solid ${color}`,
        }}
      />
    </Box>
  );
}

function getMoistureZone(percent: number): { start: number; end: number } {
  const value = clamp(percent, 0, 100);

  if (value <= 16) return { start: 0, end: 16 };
  if (value <= 32) return { start: 16, end: 32 };
  if (value <= 58) return { start: 32, end: 58 };
  if (value <= 70) return { start: 58, end: 70 };

  return { start: 70, end: 100 };
}

function getVividMoistureColor(percent: number): string {
  const value = clamp(percent, 0, 100);

  if (value <= 16) return lerpColor('#8ed400', PLATFORM_GREEN, value / 16);
  if (value <= 32) return lerpColor('#ffd800', '#f0c000', (value - 16) / 16);
  if (value <= 58) return lerpColor('#ff9a18', '#ff8810', (value - 32) / 26);
  if (value <= 70) return lerpColor('#ef3828', '#e83028', (value - 58) / 12);

  return lerpColor('#9a9498', '#8a8488', (value - 70) / 30);
}

function colorToRgba(color: string, alpha: number): string {
  const rgb = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (rgb) return `rgba(${rgb[1]}, ${rgb[2]}, ${rgb[3]}, ${alpha})`;

  const hex = color.replace('#', '');
  if (hex.length === 6) {
    const r = Number.parseInt(hex.slice(0, 2), 16);
    const g = Number.parseInt(hex.slice(2, 4), 16);
    const b = Number.parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  return `rgba(58, 52, 64, ${alpha})`;
}

function lerpColor(from: string, to: string, t: number): string {
  const ratio = clamp(t, 0, 1);
  const parse = (hex: string) => {
    const normalized = hex.replace('#', '');
    return [
      Number.parseInt(normalized.slice(0, 2), 16),
      Number.parseInt(normalized.slice(2, 4), 16),
      Number.parseInt(normalized.slice(4, 6), 16),
    ] as const;
  };

  const [r1, g1, b1] = parse(from);
  const [r2, g2, b2] = parse(to);
  const r = Math.round(r1 + (r2 - r1) * ratio);
  const g = Math.round(g1 + (g2 - g1) * ratio);
  const b = Math.round(b1 + (b2 - b1) * ratio);

  return `rgb(${r}, ${g}, ${b})`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function formatMoisture(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)}%`;
}
